import React, { useState, useMemo, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import './EtudiantsModal.css';

// ── Helpers ───────────────────────────────────────────────
const fmt = n => Number(n||0).toLocaleString('fr-FR') + ' FCFA';
function domaineName(domaines, id) { const d = domaines.find(d=>d.id===id); return d?d.name:`#${id}`; }
function domaineIcon(domaines, id) { const d = domaines.find(d=>d.id===id); return d?d.icon:'🎓'; }

// ── Photo upload button ───────────────────────────────────
function PhotoUploader({ current, onUpload, size=56, placeholder='👤' }) {
  const ref = useRef();
  function handleFile(e) {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = ev => onUpload(ev.target.result);
    r.readAsDataURL(f);
    e.target.value='';
  }
  return (
    <div className="photo-uploader" style={{width:size,height:size}} onClick={()=>ref.current.click()} title="Cliquer pour changer la photo">
      {current
        ? <img src={current} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}}/>
        : <span style={{fontSize:size*0.5}}>{placeholder}</span>
      }
      <div className="pu-overlay">📷</div>
      <input ref={ref} type="file" accept="image/*" style={{display:'none'}} onChange={handleFile}/>
    </div>
  );
}

// ── Paiement summary bar ──────────────────────────────────
function PaiementBar({ paiements=[], fraisTotal=0 }) {
  const encaisse = paiements.reduce((s,p)=>s+(+p.montant||0),0);
  const reste    = Math.max(0, fraisTotal - encaisse);
  const pct      = fraisTotal>0 ? Math.min(100, Math.round(encaisse/fraisTotal*100)) : 0;
  const couleur  = pct===100 ? '#00FF88' : pct>50 ? '#FFB800' : '#FF4060';
  return (
    <div className="paie-bar-wrap">
      <div className="paie-bar-nums">
        <span className="paie-encaisse">✓ {fmt(encaisse)}</span>
        {reste>0 && <span className="paie-reste">Reste : {fmt(reste)}</span>}
        <span className="paie-pct" style={{color:couleur}}>{pct}%</span>
      </div>
      <div className="paie-bar-track">
        <div className="paie-bar-fill" style={{width:pct+'%',background:couleur}}/>
      </div>
    </div>
  );
}

// ── Paiement detail panel ─────────────────────────────────
function PaiementPanel({ etudiant, fraisFormation, isAdmin, onSavePaiements }) {
  const [open, setOpen] = useState(false);
  const [frais, setFrais] = useState(etudiant.fraisTotal ?? fraisFormation ?? 0);
  const [paiements, setPaiements] = useState(etudiant.paiements || []);
  const [newPaie, setNewPaie] = useState({ date: new Date().toISOString().slice(0,10), montant:'', mode:'Mobile Money', note:'' });

  const encaisse = paiements.reduce((s,p)=>s+(+p.montant||0),0);
  const reste    = Math.max(0, frais - encaisse);

  function addPaie() {
    if (!newPaie.montant) return;
    const p = { id: Date.now(), ...newPaie, montant: +newPaie.montant };
    const updated = [...paiements, p];
    setPaiements(updated);
    onSavePaiements(etudiant.id, updated, frais);
    setNewPaie(n=>({...n, montant:'', note:''}));
  }
  function removePaie(id) {
    const updated = paiements.filter(p=>p.id!==id);
    setPaiements(updated);
    onSavePaiements(etudiant.id, updated, frais);
  }
  function saveFrais() { onSavePaiements(etudiant.id, paiements, frais); }

  return (
    <div className="paie-panel">
      <PaiementBar paiements={paiements} fraisTotal={frais}/>
      <button className="paie-toggle-btn" onClick={()=>setOpen(v=>!v)}>
        {open ? '▲ Fermer paiements' : '💳 Gérer paiements'}
      </button>
      {open && (
        <div className="paie-detail">
          {isAdmin && (
            <div className="paie-frais-row">
              <label>Frais de formation (FCFA)</label>
              <input type="number" value={frais} onChange={e=>setFrais(+e.target.value)} onBlur={saveFrais} placeholder="Ex: 150000"/>
            </div>
          )}
          <div className="paie-totaux">
            <div className="pt-item"><span>Total frais</span><strong>{fmt(frais)}</strong></div>
            <div className="pt-item pt-ok"><span>Encaissé</span><strong>{fmt(encaisse)}</strong></div>
            <div className="pt-item pt-warn"><span>Reste à payer</span><strong>{fmt(reste)}</strong></div>
          </div>
          <div className="paie-list">
            {paiements.length===0 && <p className="paie-empty">Aucun paiement enregistré.</p>}
            {paiements.map(p=>(
              <div key={p.id} className="paie-item">
                <span className="pi-date">📅 {p.date}</span>
                <span className="pi-montant">{fmt(p.montant)}</span>
                <span className="pi-mode">{p.mode}</span>
                {p.note && <span className="pi-note">— {p.note}</span>}
                {isAdmin && <button className="pi-del" onClick={()=>removePaie(p.id)}>✕</button>}
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="paie-add-form">
              <div className="paf-row">
                <input type="date" value={newPaie.date} onChange={e=>setNewPaie(n=>({...n,date:e.target.value}))}/>
                <input type="number" placeholder="Montant (FCFA)" value={newPaie.montant} onChange={e=>setNewPaie(n=>({...n,montant:e.target.value}))}/>
                <select value={newPaie.mode} onChange={e=>setNewPaie(n=>({...n,mode:e.target.value}))}>
                  {['Mobile Money','MTN MoMo','Moov Money','Virement','Espèces','Chèque'].map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="paf-row">
                <input placeholder="Note (optionnel)" value={newPaie.note} onChange={e=>setNewPaie(n=>({...n,note:e.target.value}))} style={{flex:1}}/>
                <button className="abtn abtn-green" style={{fontSize:12,padding:'6px 14px'}} onClick={addPaie}>+ Ajouter</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Fiche détail étudiant (modal dans modal) ──────────────
function FicheEtudiant({ etudiant, promo, domaines, isAdmin, onUpdate, onClose }) {
  const [loc, setLoc] = useState({...etudiant});
  function save(patch) {
    const updated = {...loc,...patch};
    setLoc(updated);
    onUpdate(promo.id, updated.id, patch);
  }
  const fraisFormation = parseInt((domaines.find(d=>d.id===promo.domaineId)?.ti?.[0]?.prix||'').replace(/[^\d]/g,''))||0;

  return (
    <div className="fiche-overlay" onClick={onClose}>
      <div className="fiche-card" onClick={e=>e.stopPropagation()}>
        <div className="fiche-head">
          <h3>📋 Fiche Étudiant</h3>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>
        <div className="fiche-body">
          {/* Photo + identité */}
          <div className="fiche-top">
            <PhotoUploader
              current={loc.photo} size={90}
              onUpload={data=>save({photo:data})}
            />
            <div className="fiche-identity">
              <div className="fiche-name">{loc.prenom} {loc.nom}</div>
              <div className="fiche-statuts">
                {loc.diplome && <span className="badge-diplome">🎓 Diplômé</span>}
                {loc.isAlumni && <span className="badge-alumni">🏆 Alumni</span>}
              </div>
              <div className="fiche-meta-list">
                {loc.email && <span>📧 {loc.email}</span>}
                {loc.tel   && <span>📞 {loc.tel}</span>}
                {loc.diplomeDate && <span>🗓 Diplômé le {loc.diplomeDate}</span>}
              </div>
              <div className="fiche-promo-tag">
                {domaineIcon(domaines,promo.domaineId)} {promo.titre}
              </div>
            </div>
          </div>

          {/* Paiements */}
          <div className="fiche-section-title">💳 Suivi des paiements</div>
          <PaiementPanel
            etudiant={loc}
            fraisFormation={fraisFormation}
            isAdmin={isAdmin}
            onSavePaiements={(id, paiements, fraisTotal) => save({paiements, fraisTotal})}
          />

          {/* Note alumni */}
          {loc.isAlumni && (
            <>
              <div className="fiche-section-title" style={{marginTop:14}}>🏆 Fiche Alumni</div>
              <textarea
                className="fiche-textarea"
                placeholder="Situation actuelle, entreprise, poste, témoignage…"
                value={loc.alumniNote||''}
                onChange={e=>save({alumniNote:e.target.value})}
                rows={3}
              />
            </>
          )}

          {/* Edit fields (admin) */}
          {isAdmin && (
            <>
              <div className="fiche-section-title" style={{marginTop:14}}>✏️ Modifier les informations</div>
              <div className="fiche-edit-grid">
                {[['prenom','Prénom'],['nom','Nom'],['email','Email'],['tel','Téléphone']].map(([k,l])=>(
                  <div key={k} className="fg2">
                    <label>{l}</label>
                    <input value={loc[k]||''} onChange={e=>setLoc(x=>({...x,[k]:e.target.value}))} onBlur={()=>save({[k]:loc[k]})}/>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Student row (compact with photo) ─────────────────────
function EtudiantRow({ etudiant, promoStatut, fraisFormation, isAdmin, onToggleDiplome, onSetAlumni, onDelete, onUpdateEtudiant, promo, domaines }) {
  const [showFiche, setShowFiche] = useState(false);
  const [showAlumni, setShowAlumni] = useState(false);
  const [alumniNote, setAlumniNote] = useState(etudiant.alumniNote||'');

  const encaisse = (etudiant.paiements||[]).reduce((s,p)=>s+(+p.montant||0),0);
  const frais    = etudiant.fraisTotal ?? fraisFormation ?? 0;
  const reste    = Math.max(0, frais - encaisse);

  return (
    <>
      <div className={'etud-row'+(etudiant.diplome?' etud-diplome':'')+(etudiant.isAlumni?' etud-alumni':'')}>
        {/* Photo identité */}
        <div className="etud-photo-wrap" onClick={()=>setShowFiche(true)} title="Ouvrir la fiche">
          {etudiant.photo
            ? <img src={etudiant.photo} alt="" className="etud-photo-img"/>
            : <div className="etud-photo-placeholder">{etudiant.isAlumni?'🏆':etudiant.diplome?'🎓':'👤'}</div>
          }
        </div>

        <div className="etud-info" style={{flex:1}}>
          <div className="etud-name-row">
            <span className="etud-name" onClick={()=>setShowFiche(true)} style={{cursor:'pointer'}} title="Voir la fiche complète">
              {etudiant.prenom} {etudiant.nom}
            </span>
            {etudiant.isAlumni && <span className="etud-alumni-badge">ALUMNI</span>}
            {etudiant.diplome  && !etudiant.isAlumni && <span className="etud-diplome-badge">DIPLÔMÉ</span>}
          </div>
          <div className="etud-meta">
            {etudiant.email && <span>📧 {etudiant.email}</span>}
            {etudiant.tel   && <span>📞 {etudiant.tel}</span>}
            {etudiant.diplomeDate && <span>🎓 {etudiant.diplomeDate}</span>}
          </div>
          {/* Mini paiement bar */}
          {frais>0 && (
            <div className="etud-mini-paie">
              <span className="emp-encaisse">{fmt(encaisse)}</span>
              {reste>0 && <span className="emp-reste">/ reste {fmt(reste)}</span>}
              <div className="emp-bar"><div className="emp-fill" style={{width:Math.min(100,frais>0?Math.round(encaisse/frais*100):0)+'%'}}/></div>
            </div>
          )}
          {etudiant.alumniNote && <div className="etud-alumni-note">💬 {etudiant.alumniNote}</div>}
          {showAlumni && (
            <div className="etud-alumni-form">
              <input placeholder="Situation, entreprise, poste…" value={alumniNote} onChange={e=>setAlumniNote(e.target.value)}/>
              <div style={{display:'flex',gap:6,marginTop:5}}>
                <button className="abtn abtn-green" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>{onSetAlumni(etudiant.id,alumniNote);setShowAlumni(false);}}>✓</button>
                <button className="abtn abtn-muted" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>setShowAlumni(false)}>✕</button>
              </div>
            </div>
          )}
        </div>

        <div className="etud-actions">
          <button className="etud-btn etud-btn-fiche" onClick={()=>setShowFiche(true)} title="Fiche complète">📋</button>
          {isAdmin && promoStatut==='terminee' && !etudiant.diplome && (
            <button className="etud-btn etud-btn-diplome" title="Diplômer" onClick={()=>onToggleDiplome(etudiant.id,true)}>🎓</button>
          )}
          {isAdmin && etudiant.diplome && (
            <button className="etud-btn etud-btn-undiplome" title="Retirer diplôme" onClick={()=>onToggleDiplome(etudiant.id,false)}>↩</button>
          )}
          {isAdmin && etudiant.diplome && (
            <button className={'etud-btn '+(etudiant.isAlumni?'etud-btn-alumni-active':'etud-btn-alumni')} title="Alumni" onClick={()=>setShowAlumni(v=>!v)}>🏆</button>
          )}
          {isAdmin && (
            <button className="etud-btn etud-btn-del" title="Supprimer" onClick={()=>onDelete(etudiant.id)}>✕</button>
          )}
        </div>
      </div>

      {showFiche && (
        <FicheEtudiant
          etudiant={etudiant}
          promo={promo}
          domaines={domaines}
          isAdmin={isAdmin}
          onUpdate={onUpdateEtudiant}
          onClose={()=>setShowFiche(false)}
        />
      )}
    </>
  );
}

// ── StatutBadge ───────────────────────────────────────────
function StatutBadge({ statut }) {
  const map = {
    en_cours: { label:'En cours', color:'#00BFFF', bg:'rgba(0,191,255,0.1)' },
    terminee: { label:'Terminée', color:'#00FF88', bg:'rgba(0,255,136,0.1)' },
  };
  const s = map[statut]||map.en_cours;
  return <span style={{fontSize:11,fontWeight:700,padding:'2px 9px',borderRadius:100,color:s.color,background:s.bg,border:`1px solid ${s.color}40`}}>{s.label}</span>;
}

// ── PromoCard ─────────────────────────────────────────────
function PromoCard({ promo, domaines, isAdmin, onUpdatePromo, onDeletePromo, onAddEtudiant, onUpdateEtudiant, showToast }) {
  const [expanded,  setExpanded]  = useState(false);
  const [addForm,   setAddForm]   = useState(false);
  const [nouv, setNouv] = useState({nom:'',prenom:'',email:'',tel:''});
  const [editPromo, setEditPromo] = useState(false);
  const [editData,  setEditData]  = useState({titre:promo.titre,dateDebut:promo.dateDebut,dateFin:promo.dateFin,statut:promo.statut,domaineId:promo.domaineId});
  const [filterTab, setFilterTab] = useState('all');

  const etudiants = promo.etudiants||[];
  const counts = {
    total:   etudiants.length,
    diplome: etudiants.filter(e=>e.diplome).length,
    alumni:  etudiants.filter(e=>e.isAlumni).length,
    enCours: etudiants.filter(e=>!e.diplome).length,
  };

  // Frais de la formation correspondante (1er TI du domaine)
  const dom = domaines.find(d=>d.id===promo.domaineId);
  const fraisFormation = parseInt((dom?.ti?.[0]?.prix||'').replace(/[^\d]/g,''))||0;

  const totalEncaisse = etudiants.reduce((s,e)=>
    s + (e.paiements||[]).reduce((ss,p)=>ss+(+p.montant||0),0)
  ,0);
  const totalAttendu  = etudiants.reduce((s,e)=>s+(e.fraisTotal??fraisFormation??0),0);
  const totalReste    = Math.max(0, totalAttendu-totalEncaisse);

  const filtered = filterTab==='diplome'  ? etudiants.filter(e=>e.diplome&&!e.isAlumni)
                 : filterTab==='alumni'   ? etudiants.filter(e=>e.isAlumni)
                 : filterTab==='en_cours' ? etudiants.filter(e=>!e.diplome)
                 : etudiants;

  function saveAdd() {
    if (!nouv.nom.trim()&&!nouv.prenom.trim()) { showToast('Nom requis','err'); return; }
    onAddEtudiant(promo.id, { id:Date.now(), ...nouv, diplome:false, diplomeDate:'', isAlumni:false, alumniNote:'', photo:null, paiements:[], fraisTotal: fraisFormation });
    setNouv({nom:'',prenom:'',email:'',tel:''});
    setAddForm(false);
  }
  function handleToggleDiplome(etudId,val) {
    const date = val ? new Date().toLocaleDateString('fr-FR') : '';
    onUpdateEtudiant(promo.id,etudId,{diplome:val,diplomeDate:date});
    showToast(val?'🎓 Diplôme accordé !':'Diplôme retiré.',val?'ok':'warn');
  }
  function handleSetAlumni(etudId,note) {
    onUpdateEtudiant(promo.id,etudId,{isAlumni:true,alumniNote:note});
    showToast('🏆 Ajouté aux Alumni !');
  }
  function handleDeleteEtudiant(etudId) {
    if (!window.confirm('Supprimer cet étudiant ?')) return;
    onUpdateEtudiant(promo.id,etudId,null);
  }
  function handleUpdateEtudiant(promoId,etudId,patch) {
    onUpdateEtudiant(promoId,etudId,patch);
  }
  function saveEditPromo() {
    onUpdatePromo(promo.id,editData);
    setEditPromo(false);
    showToast('Promotion mise à jour.');
  }

  return (
    <div className={'promo-card'+(promo.statut==='terminee'?' promo-done':'')}>
      <div className="promo-head" onClick={()=>setExpanded(v=>!v)}>
        <div className="promo-left">
          <span className="promo-domain-icon">{domaineIcon(domaines,promo.domaineId)}</span>
          <div>
            <div className="promo-titre">{promo.titre}</div>
            <div className="promo-domain-name">{domaineName(domaines,promo.domaineId)}</div>
            <div className="promo-dates">📅 {promo.dateDebut} → {promo.dateFin||'…'}</div>
          </div>
        </div>
        <div className="promo-right">
          <div className="promo-counts">
            <span className="pc pc-total">👥 {counts.total}</span>
            {counts.diplome>0 && <span className="pc pc-diplome">🎓 {counts.diplome}</span>}
            {counts.alumni>0  && <span className="pc pc-alumni">🏆 {counts.alumni}</span>}
          </div>
          {totalAttendu>0 && (
            <div className="promo-finance-mini" title="Finances de la promotion">
              <span style={{color:'#00FF88',fontSize:11}}>✓{fmt(totalEncaisse)}</span>
              {totalReste>0 && <span style={{color:'#FF4060',fontSize:11}}>/{fmt(totalReste)}</span>}
            </div>
          )}
          <StatutBadge statut={promo.statut}/>
          {isAdmin && <button className="promo-edit-btn" title="Modifier" onClick={e=>{e.stopPropagation();setEditPromo(v=>!v);}}>✏</button>}
          {isAdmin && <button className="promo-del-btn"  title="Supprimer" onClick={e=>{e.stopPropagation();if(window.confirm('Supprimer cette promotion ?'))onDeletePromo(promo.id);}}>🗑</button>}
          <span className="promo-toggle">{expanded?'▲':'▼'}</span>
        </div>
      </div>

      {editPromo && isAdmin && (
        <div className="promo-edit-form">
          <div className="pef-grid">
            <div className="fg2"><label>Titre</label><input value={editData.titre} onChange={e=>setEditData(d=>({...d,titre:e.target.value}))}/></div>
            <div className="fg2"><label>Domaine</label>
              <select value={editData.domaineId} onChange={e=>setEditData(d=>({...d,domaineId:+e.target.value}))}>
                {domaines.map(d=><option key={d.id} value={d.id}>{d.icon} {d.name}</option>)}
              </select>
            </div>
            <div className="fg2"><label>Début</label><input type="date" value={editData.dateDebut} onChange={e=>setEditData(d=>({...d,dateDebut:e.target.value}))}/></div>
            <div className="fg2"><label>Fin</label><input type="date" value={editData.dateFin} onChange={e=>setEditData(d=>({...d,dateFin:e.target.value}))}/></div>
            <div className="fg2"><label>Statut</label>
              <select value={editData.statut} onChange={e=>setEditData(d=>({...d,statut:e.target.value}))}>
                <option value="en_cours">En cours</option>
                <option value="terminee">Terminée</option>
              </select>
            </div>
          </div>
          <div style={{display:'flex',gap:8,marginTop:10}}>
            <button className="abtn abtn-green" style={{fontSize:12}} onClick={saveEditPromo}>✓ Enregistrer</button>
            <button className="abtn abtn-muted" style={{fontSize:12}} onClick={()=>setEditPromo(false)}>Annuler</button>
          </div>
        </div>
      )}

      {expanded && (
        <div className="promo-body">
          <div className="promo-tabs">
            {[['all',`Tous (${counts.total})`],['en_cours',`En cours (${counts.enCours})`],['diplome',`Diplômés (${counts.diplome})`],['alumni',`Alumni (${counts.alumni})`]].map(([t,l])=>(
              <button key={t} className={'promo-tab'+(filterTab===t?' active':'')} onClick={()=>setFilterTab(t)}>{l}</button>
            ))}
          </div>
          <div className="etud-list">
            {filtered.length===0
              ? <div className="etud-empty">Aucun étudiant dans cette catégorie.</div>
              : filtered.map(e=>(
                <EtudiantRow
                  key={e.id} etudiant={e}
                  promoStatut={promo.statut}
                  fraisFormation={fraisFormation}
                  isAdmin={isAdmin}
                  promo={promo} domaines={domaines}
                  onToggleDiplome={handleToggleDiplome}
                  onSetAlumni={handleSetAlumni}
                  onDelete={handleDeleteEtudiant}
                  onUpdateEtudiant={handleUpdateEtudiant}
                />
              ))
            }
          </div>
          {isAdmin && (
            <div className="add-etud">
              {!addForm
                ? <button className="abtn abtn-green" style={{fontSize:12}} onClick={()=>setAddForm(true)}>+ Ajouter un étudiant</button>
                : (
                  <div className="add-etud-form">
                    <div className="pef-grid">
                      <div className="fg2"><label>Prénom *</label><input value={nouv.prenom} onChange={e=>setNouv(n=>({...n,prenom:e.target.value}))} placeholder="Prénom"/></div>
                      <div className="fg2"><label>Nom *</label><input value={nouv.nom} onChange={e=>setNouv(n=>({...n,nom:e.target.value}))} placeholder="Nom"/></div>
                      <div className="fg2"><label>Email</label><input value={nouv.email} onChange={e=>setNouv(n=>({...n,email:e.target.value}))} placeholder="email@ex.com"/></div>
                      <div className="fg2"><label>Téléphone</label><input value={nouv.tel} onChange={e=>setNouv(n=>({...n,tel:e.target.value}))} placeholder="+229…"/></div>
                    </div>
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button className="abtn abtn-green" style={{fontSize:12}} onClick={saveAdd}>✓ Ajouter</button>
                      <button className="abtn abtn-muted" style={{fontSize:12}} onClick={()=>setAddForm(false)}>Annuler</button>
                    </div>
                  </div>
                )
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────
export default function EtudiantsModal({ onClose }) {
  const { data, updateData, currentUser, showToast } = useApp();
  const isAdmin   = currentUser?.role==='admin';
  const isManager = currentUser?.role==='manager';

  const promotions = useMemo(() => data.promotions || [], [data.promotions]);
  const domaines   = useMemo(() => data.domaines   || [], [data.domaines]);

  const [filterDomaine, setFilterDomaine] = useState('all');
  const [filterStatut,  setFilterStatut]  = useState('all');
  const [search,        setSearch]        = useState('');
  const [viewTab,       setViewTab]       = useState('promotions');
  const [newPromo,      setNewPromo]      = useState({ titre:'', domaineId:domaines[0]?.id||1, dateDebut:'', dateFin:'', statut:'en_cours' });

  const stats = useMemo(()=>{
    const all = promotions.flatMap(p=>p.etudiants||[]);
    const totalAttendu  = promotions.flatMap(p=>p.etudiants||[]).reduce((s,e)=>s+(e.fraisTotal??0),0);
    const totalEncaisse = all.reduce((s,e)=>s+(e.paiements||[]).reduce((ss,p)=>ss+(+p.montant||0),0),0);
    return {
      promos:  promotions.length,
      inscrits:all.length,
      diplomes:all.filter(e=>e.diplome).length,
      alumni:  all.filter(e=>e.isAlumni).length,
      enCours: promotions.filter(p=>p.statut==='en_cours').length,
      totalAttendu, totalEncaisse,
      totalReste: Math.max(0,totalAttendu-totalEncaisse),
    };
  },[promotions]);

  const filtered = useMemo(()=>{
    let list=[...promotions];
    if (filterDomaine!=='all') list=list.filter(p=>p.domaineId===+filterDomaine);
    if (filterStatut!=='all')  list=list.filter(p=>p.statut===filterStatut);
    if (search.trim()) {
      const q=search.toLowerCase();
      list=list.filter(p=>p.titre.toLowerCase().includes(q)||domaineName(domaines,p.domaineId).toLowerCase().includes(q)||(p.etudiants||[]).some(e=>(e.nom+' '+e.prenom).toLowerCase().includes(q)||(e.email||'').toLowerCase().includes(q)));
    }
    return list;
  },[promotions,filterDomaine,filterStatut,search,domaines]);

  const allDiplomes = useMemo(()=>promotions.flatMap(p=>(p.etudiants||[]).filter(e=>e.diplome).map(e=>({...e,promoTitre:p.titre,domaineId:p.domaineId,promoId:p.id}))).filter(e=>filterDomaine==='all'||e.domaineId===+filterDomaine),[promotions,filterDomaine]);
  const allAlumni   = allDiplomes.filter(e=>e.isAlumni);

  // Access guard — after all hooks
  if (!isAdmin && !isManager) return null;

  function addPromotion() {
    if (!newPromo.titre.trim()) { showToast('Titre requis','err'); return; }
    updateData({ promotions:[...promotions,{...newPromo,id:Date.now(),etudiants:[]}] });
    setNewPromo({titre:'',domaineId:domaines[0]?.id||1,dateDebut:'',dateFin:'',statut:'en_cours'});
    setViewTab('promotions');
    showToast('Promotion créée !');
  }
  function updatePromo(id,patch) { updateData({promotions:promotions.map(p=>p.id===id?{...p,...patch}:p)}); }
  function deletePromo(id)       { updateData({promotions:promotions.filter(p=>p.id!==id)}); showToast('Promotion supprimée.','warn'); }
  function addEtudiant(promoId,etud) {
    updateData({promotions:promotions.map(p=>p.id===promoId?{...p,etudiants:[...(p.etudiants||[]),etud]}:p)});
    showToast('Étudiant ajouté !');
  }
  function updateEtudiant(promoId,etudId,patch) {
    updateData({
      promotions:promotions.map(p=>{
        if (p.id!==promoId) return p;
        if (patch===null) return {...p,etudiants:(p.etudiants||[]).filter(e=>e.id!==etudId)};
        return {...p,etudiants:(p.etudiants||[]).map(e=>e.id===etudId?{...e,...patch}:e)};
      }),
    });
  }

  return (
    <div className="overlay open">
      <div className="modal modal-xl">
        <div className="mhead">
          <div>
            <h2>🎓 Gestion Étudiants &amp; Alumni</h2>
            <p style={{fontSize:13,color:'var(--muted)',marginTop:3}}>
              {isAdmin?'🔐 Admin — gestion complète':'📋 Manager — consultation'}
            </p>
          </div>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>

        {/* KPIs */}
        <div className="etud-kpis">
          {[
            {val:stats.promos,   lbl:'Promotions',  color:'var(--primary)'},
            {val:stats.enCours,  lbl:'En cours',    color:'#00BFFF'},
            {val:stats.inscrits, lbl:'Inscrits',    color:'var(--text)'},
            {val:stats.diplomes, lbl:'Diplômés',    color:'#00FF88'},
            {val:stats.alumni,   lbl:'Alumni',      color:'#FFB800'},
          ].map(k=>(
            <div key={k.lbl} className="etud-kpi">
              <div className="etud-kpi-val" style={{color:k.color}}>{k.val}</div>
              <div className="etud-kpi-lbl">{k.lbl}</div>
            </div>
          ))}
          <div className="etud-kpi">
            <div className="etud-kpi-val" style={{color:'#00FF88',fontSize:13}}>{fmt(stats.totalEncaisse)}</div>
            <div className="etud-kpi-lbl">Encaissé</div>
          </div>
          <div className="etud-kpi">
            <div className="etud-kpi-val" style={{color:'#FF4060',fontSize:13}}>{fmt(stats.totalReste)}</div>
            <div className="etud-kpi-lbl">Reste à payer</div>
          </div>
        </div>

        {/* View tabs */}
        <div className="etud-view-tabs">
          {[['promotions','📋 Promotions'],['diplomes','🎓 Tous diplômés'],['alumni','🏆 Alumni'],...(isAdmin?[['new','+ Nouvelle promotion']]:[])]
            .map(([t,l])=>(
              <button key={t} className={'etud-vtab'+(viewTab===t?' active':'')} onClick={()=>setViewTab(t)}>{l}</button>
            ))
          }
        </div>

        {/* Filters */}
        {viewTab!=='new' && (
          <div className="etud-filters">
            <input className="etud-search" placeholder="🔍 Titre, étudiant, email…" value={search} onChange={e=>setSearch(e.target.value)}/>
            <select value={filterDomaine} onChange={e=>setFilterDomaine(e.target.value)}>
              <option value="all">🎯 Tous domaines</option>
              {domaines.map(d=><option key={d.id} value={d.id}>{d.icon} {d.name}</option>)}
            </select>
            {viewTab==='promotions' && (
              <select value={filterStatut} onChange={e=>setFilterStatut(e.target.value)}>
                <option value="all">🔘 Tous statuts</option>
                <option value="en_cours">En cours</option>
                <option value="terminee">Terminée</option>
              </select>
            )}
          </div>
        )}

        {/* Content */}
        <div className="etud-content">

          {/* PROMOTIONS */}
          {viewTab==='promotions' && (
            filtered.length===0
              ? <div className="etud-empty-main"><div style={{fontSize:52}}>🎓</div><p>Aucune promotion trouvée</p>{isAdmin&&<p style={{fontSize:13,color:'var(--muted)',marginTop:6}}>Créez votre première promotion via l'onglet + Nouvelle promotion</p>}</div>
              : filtered.map(promo=>(
                <PromoCard key={promo.id} promo={promo} domaines={domaines} isAdmin={isAdmin}
                  onUpdatePromo={updatePromo} onDeletePromo={deletePromo}
                  onAddEtudiant={addEtudiant} onUpdateEtudiant={updateEtudiant} showToast={showToast}/>
              ))
          )}

          {/* DIPLÔMÉS */}
          {viewTab==='diplomes' && (
            <div>
              <div className="etud-global-header">🎓 <strong>{allDiplomes.length}</strong> diplômé(s)</div>
              {allDiplomes.length===0
                ? <div className="etud-empty-main"><div style={{fontSize:52}}>🎓</div><p>Aucun diplômé enregistré</p></div>
                : domaines.filter(d=>filterDomaine==='all'||d.id===+filterDomaine).map(d=>{
                    const items=allDiplomes.filter(e=>e.domaineId===d.id);
                    if(!items.length) return null;
                    return (
                      <div key={d.id} className="diplome-group">
                        <div className="dg-header">{d.icon} {d.name} — {items.length} diplômé(s)</div>
                        {items.map(e=>(
                          <div key={e.id} className="diplome-row">
                            <div className="dr-photo">
                              {e.photo ? <img src={e.photo} alt=""/> : <span>{e.isAlumni?'🏆':'🎓'}</span>}
                            </div>
                            <div className="dr-info">
                              <strong>{e.prenom} {e.nom}</strong>
                              <span className="dr-promo">Promo : {e.promoTitre}</span>
                              <span className="dr-date">🗓 {e.diplomeDate}</span>
                              {e.email&&<span>{e.email}</span>}
                            </div>
                            {e.isAlumni&&<span className="etud-alumni-badge">ALUMNI</span>}
                          </div>
                        ))}
                      </div>
                    );
                  })
              }
            </div>
          )}

          {/* ALUMNI */}
          {viewTab==='alumni' && (
            <div>
              <div className="etud-global-header">🏆 <strong>{allAlumni.length}</strong> alumni</div>
              {allAlumni.length===0
                ? <div className="etud-empty-main"><div style={{fontSize:52}}>🏆</div><p>Aucun alumni enregistré</p><p style={{fontSize:13,color:'var(--muted)',marginTop:6}}>Marquez un diplômé comme Alumni depuis sa promotion.</p></div>
                : domaines.filter(d=>filterDomaine==='all'||d.id===+filterDomaine).map(d=>{
                    const items=allAlumni.filter(e=>e.domaineId===d.id);
                    if(!items.length) return null;
                    return (
                      <div key={d.id} className="diplome-group">
                        <div className="dg-header">{d.icon} {d.name} — {items.length} alumni</div>
                        {items.map(e=>(
                          <div key={e.id} className="alumni-card">
                            <div className="ac-photo">
                              {e.photo ? <img src={e.photo} alt=""/> : <span>🏆</span>}
                            </div>
                            <div className="ac-info">
                              <div className="ac-name">{e.prenom} {e.nom}</div>
                              <div className="ac-meta">
                                <span>Promo : {e.promoTitre}</span>
                                <span>🎓 {e.diplomeDate}</span>
                                {e.email&&<span>📧 {e.email}</span>}
                                {e.tel&&<span>📞 {e.tel}</span>}
                              </div>
                              {e.alumniNote&&<div className="ac-note">💬 {e.alumniNote}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })
              }
            </div>
          )}

          {/* NOUVELLE PROMO */}
          {viewTab==='new' && isAdmin && (
            <div className="new-promo-form">
              <h3>➕ Créer une nouvelle promotion</h3>
              <div className="pef-grid">
                <div className="fg2" style={{gridColumn:'1/-1'}}><label>Titre *</label><input value={newPromo.titre} onChange={e=>setNewPromo(n=>({...n,titre:e.target.value}))} placeholder="Ex: Promotion Web 2025, Cohorte Data Janvier 2026…"/></div>
                <div className="fg2"><label>Domaine *</label><select value={newPromo.domaineId} onChange={e=>setNewPromo(n=>({...n,domaineId:+e.target.value}))}>{domaines.map(d=><option key={d.id} value={d.id}>{d.icon} {d.name}</option>)}</select></div>
                <div className="fg2"><label>Statut</label><select value={newPromo.statut} onChange={e=>setNewPromo(n=>({...n,statut:e.target.value}))}><option value="en_cours">En cours</option><option value="terminee">Terminée</option></select></div>
                <div className="fg2"><label>Date début</label><input type="date" value={newPromo.dateDebut} onChange={e=>setNewPromo(n=>({...n,dateDebut:e.target.value}))}/></div>
                <div className="fg2"><label>Date fin</label><input type="date" value={newPromo.dateFin} onChange={e=>setNewPromo(n=>({...n,dateFin:e.target.value}))}/></div>
              </div>
              <button className="abtn abtn-green" style={{marginTop:16}} onClick={addPromotion}>✓ Créer la promotion</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
